import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { createTheme, Theme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { BRAND } from '../config/brand';

// localStorage anahtarları
const THEME_PRIMARY_KEY = 'gts_theme_primary';
const THEME_SECONDARY_KEY = 'gts_theme_secondary';
const THEME_ACCENT_KEY = 'gts_theme_accent';

// Varsayılan renkler (BRAND'den alınır)
const DEFAULT_PRIMARY = BRAND.colors.primary;
const DEFAULT_SECONDARY = BRAND.colors.secondary;
const DEFAULT_ACCENT = BRAND.colors.accent;

interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
}

interface ThemeContextType {
  colors: ThemeColors;
  setPrimaryColor: (color: string) => void;
  setSecondaryColor: (color: string) => void;
  setAccentColor: (color: string) => void;
  resetToDefaults: () => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// localStorage'dan renkleri oku
const getStoredColors = (): ThemeColors => {
  return {
    primary: localStorage.getItem(THEME_PRIMARY_KEY) || DEFAULT_PRIMARY,
    secondary: localStorage.getItem(THEME_SECONDARY_KEY) || DEFAULT_SECONDARY,
    accent: localStorage.getItem(THEME_ACCENT_KEY) || DEFAULT_ACCENT,
  };
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [colors, setColors] = useState<ThemeColors>(getStoredColors);

  // Primary renk değiştir
  const setPrimaryColor = (color: string) => {
    setColors(prev => ({ ...prev, primary: color }));
    localStorage.setItem(THEME_PRIMARY_KEY, color);
  };

  // Secondary renk değiştir
  const setSecondaryColor = (color: string) => {
    setColors(prev => ({ ...prev, secondary: color }));
    localStorage.setItem(THEME_SECONDARY_KEY, color);
  };

  // Accent renk değiştir
  const setAccentColor = (color: string) => {
    setColors(prev => ({ ...prev, accent: color }));
    localStorage.setItem(THEME_ACCENT_KEY, color);
  };

  // Varsayılana dön
  const resetToDefaults = () => {
    const defaults = {
      primary: DEFAULT_PRIMARY,
      secondary: DEFAULT_SECONDARY,
      accent: DEFAULT_ACCENT,
    };
    setColors(defaults);
    localStorage.setItem(THEME_PRIMARY_KEY, DEFAULT_PRIMARY);
    localStorage.setItem(THEME_SECONDARY_KEY, DEFAULT_SECONDARY);
    localStorage.setItem(THEME_ACCENT_KEY, DEFAULT_ACCENT);
  };

  // Dinamik tema oluştur (brandTheme ile aynı ayarlar)
  const theme = useMemo(() => createTheme({
    palette: {
      primary: {
        main: colors.primary,
      },
      secondary: {
        main: colors.secondary,
      },
      info: {
        main: colors.accent,
      },
      background: {
        default: '#F5F7FA',
      },
    },
    typography: {
      fontFamily: [
        'Poppins',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif'
      ].join(','),
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      // AI asistanı herhangi bir modal açıkken de kullanılabilmeli: MUI Dialog'un
      // odak tuzağı (enforceFocus) chat penceresine (sabit Paper, Dialog portal'ı
      // dışında) odak verilmesini engelliyordu. Esc/backdrop davranışı değişmez.
      MuiDialog: {
        defaultProps: {
          disableEnforceFocus: true,
        },
      },
    },
  }), [colors]);

  // CSS değişkenlerini güncelle (gradientler için)
  useEffect(() => {
    document.documentElement.style.setProperty('--theme-primary', colors.primary);
    document.documentElement.style.setProperty('--theme-secondary', colors.secondary);
    document.documentElement.style.setProperty('--theme-accent', colors.accent);
  }, [colors]);

  const value = {
    colors,
    setPrimaryColor,
    setSecondaryColor,
    setAccentColor,
    resetToDefaults,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook
export const useThemeContext = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

// Export constants for use in other files
export const THEME_KEYS = {
  PRIMARY: THEME_PRIMARY_KEY,
  SECONDARY: THEME_SECONDARY_KEY,
  ACCENT: THEME_ACCENT_KEY,
};

export const DEFAULT_THEME_COLORS = {
  primary: DEFAULT_PRIMARY,
  secondary: DEFAULT_SECONDARY,
  accent: DEFAULT_ACCENT,
};
